import {
  action,
  atom,
  noop,
  reatomAsync,
  sleep,
  withAbort,
} from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { searchPairRequestAction } from '@root/entities/pair'
import { pairRegistry } from '@root/entities/pair/atoms'
import { type Error } from '@root/shared'

import type { PairModel } from '@root/entities/pair'

const MIN_LENGTH = 2

const DEBOUNCE_DELAY = 150

export const reatomSearchPair = () => {
  const searchStateAtom = atom(
    AsyncStateN.notInitiated<Error.UnknownError, Array<PairModel>>(),
    'searchStateAtom',
  )
  const searchQueryAtom = atom('', 'searchQueryAtom')

  const setQueryAction = action((ctx, query: string) => {
    searchQueryAtom(ctx, query)
    searchAction(ctx)
  }, 'setQueryAction')

  const effectiveQueryAtom = atom('', 'effectiveQueryAtom')

  const searchAction = reatomAsync(async (ctx) => {
    const query = ctx.get(searchQueryAtom)
    const isValid = query.length >= MIN_LENGTH
    if (!isValid) {
      searchStateAtom(ctx, AsyncStateN.notInitiated())
      effectiveQueryAtom(ctx, '')

      return
    }

    searchStateAtom(ctx, AsyncStateN.loading())
    effectiveQueryAtom(ctx, '')

    await ctx.schedule(() => sleep(DEBOUNCE_DELAY))
    effectiveQueryAtom(ctx, query)

    const network = ctx.get(selectedNetworkAtom)

    searchStateAtom(ctx, AsyncStateN.loading())

    const result = await ctx.schedule(() =>
      searchPairRequestAction(ctx, {
        network,
        query,
      }),
    )

    const mappedResult = result.pipe(
      Result.map((it) =>
        it.map((dto) => pairRegistry.resolveFromDto(ctx, dto)),
      ),
    )

    searchStateAtom(ctx, AsyncStateN.fromResult(mappedResult))
  }, 'searchAction').pipe(withAbort())

  return {
    search: searchAction,
    searchStateAtom,
    searchQueryAtom,
    setQueryAction,
  }
}

export type SearchPairModel = ReturnType<typeof reatomSearchPair>

export const openSearchModalEvent = action(noop, 'openSearchModalEvent')
